import { useEffect, useRef, useState } from 'react';
import {
  DropDown,
  FlexColumn,
  TextField,
  FormControls,
  ChipList,
  InfoTooltip,
} from '@elementary/common-ui/components';
import { Tooltip, Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { AddTestForm } from 'shared/types';
import { describeAnomalyTimeWindow } from 'shared/utils/anomalyTimeWindow';
import { TIME_BUCKET_ONLY_REASON } from 'testManagement/utils/wizardTimestampFields';
import { fontWeight, space } from '@elementary/common-ui/style/utils';
import { NO_TIMESTAMP_COLUMN_HINT } from '../constants';
import SeveritySelector from '../SeveritySelector';
import { applyGeneratedDescription } from '../utils';
import WhereClauseControl from '../WhereClauseControl';
import TableTestDescriptionField from './shared/TableTestDescriptionField';
import TestMetadataForm from './shared/TestMetadataForm';
import TimestampColumnFormField from './shared/TimestampColumnFormField';
import AnomalyTimeWindowFields from './shared/AnomalyTimeWindowFields';

const detectionTypeOptions = [
  { value: 'anomaly', label: 'anomalies' },
  { value: 'drop', label: 'drops' },
  { value: 'spike', label: 'spikes' },
];

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
  timestampColumns: { label: string; value: string }[];
};

const TablesDimensionAnomalies = ({ methods, timestampColumns }: Props) => {
  const { register, control, watch } = methods;
  const lastGeneratedDescription = useRef('');

  const [expanded, setExpanded] = useState(true);

  const detectionPeriod = watch('detectionPeriod');
  const detectionUnit = watch('detectionUnit');
  const trainingPeriodCount = watch('trainingPeriodCount');
  const trainingPeriodUnit = watch('trainingPeriodUnit');
  const detectionEventType = watch('detectionEventType');
  const timestampColumn = watch('timestampColumn');
  const timeBucketDisabledReason = timestampColumn
    ? undefined
    : TIME_BUCKET_ONLY_REASON;

  const detectionEventTypeLabel = detectionTypeOptions
    .find((o) => o.value === detectionEventType)
    ?.label.toLowerCase();
  const timeWindow = describeAnomalyTimeWindow({
    detectionPeriod,
    detectionUnit,
    trainingPeriodCount,
    trainingPeriodUnit,
  });

  useEffect(() => {
    const comparison = timestampColumn
      ? `each ${timeWindow?.bucket} bucket of the ${timestampColumn} column`
      : 'the row count of each dimension value on each run';
    const generated =
      timeWindow && detectionEventType
        ? `Checking for ${detectionEventTypeLabel} in the values distribution of the selected dimensions by comparing ${comparison} with the previous ${timeWindow.trainingPeriod}`
        : '';
    applyGeneratedDescription(methods, generated, lastGeneratedDescription);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    detectionPeriod,
    detectionUnit,
    trainingPeriodCount,
    trainingPeriodUnit,
    detectionEventType,
    timestampColumn,
  ]);

  return (
    <FormControls.TestAccordion
      summary="Dimension anomalies"
      expanded={expanded}
      onChange={(_, val) => setExpanded(val)}
    >
      <FlexColumn sx={{ gap: 3, p: 3 }}>
        <FormControls.FieldsWrapper>
          <Typography sx={{ fontWeight: fontWeight('medium') }}>
            Test configuration
          </Typography>

          <TimestampColumnFormField
            control={control}
            options={timestampColumns}
            isOptional
            optionalHint={NO_TIMESTAMP_COLUMN_HINT}
          />

          <FormControls.Row>
            <FormControls.FormText>
              The test will count rows grouped by the following columns:
            </FormControls.FormText>
            <Controller
              name="columnGroup"
              control={control}
              render={({ field }) => (
                <ChipList
                  {...field}
                  buttonText="Add value"
                  placeholder="column name"
                />
              )}
            />
          </FormControls.Row>
          <FormControls.Row>
            <FormControls.FormText>Detect</FormControls.FormText>
            <Controller
              name="detectionEventType"
              control={control}
              defaultValue="anomaly"
              render={({ field }) => (
                <DropDown
                  size="large"
                  options={detectionTypeOptions}
                  {...field}
                />
              )}
            />
            <FormControls.FormText>
              in the values distribution of the selected dimensions
            </FormControls.FormText>
          </FormControls.Row>
          <AnomalyTimeWindowFields
            methods={methods}
            timeBucketDisabledReason={timeBucketDisabledReason}
          />
          <Controller
            name="severity"
            control={methods.control}
            render={({ field }) => <SeveritySelector {...field} />}
            defaultValue={{ type: 'error' }}
          />
        </FormControls.FieldsWrapper>

        <FormControls.Divider />

        <TableTestDescriptionField methods={methods} />

        <FormControls.FieldsWrapper>
          <FormControls.TestAccordion
            variant="secondary"
            summary={
              <Typography sx={{ fontWeight: fontWeight('medium') }}>
                Advanced configuration
              </Typography>
            }
          >
            <FlexColumn sx={{ pt: '18px', gap: space('xxl') }}>
              <FormControls.Row>
                <FormControls.FormText>Sensitivity:</FormControls.FormText>
                <TextField
                  sx={{ width: '50px' }}
                  defaultValue={3}
                  {...register('sensitivity')}
                />
                <InfoTooltip title="The number of standard deviations from the mean. A higher number increases the sensitivity, while a lower number decreases it. We recommend a range of 2 to 3.5 for optimal sensitivity" />
              </FormControls.Row>
              {watch('detectionUnit') === 'day' && (
                <FormControls.Row>
                  <FormControls.FormText>
                    Enable seasonality:
                  </FormControls.FormText>
                  <Controller
                    name="seasonality"
                    control={control}
                    defaultValue="no"
                    render={({ field }) => (
                      <DropDown
                        size="large"
                        options={[
                          { value: 'yes', label: 'Yes' },
                          { value: 'no', label: 'No' },
                        ]}
                        {...field}
                      />
                    )}
                  />
                  <InfoTooltip title="If enabled, daily values will be compared exclusively to the corresponding previous values of the same weekday" />
                </FormControls.Row>
              )}
              <FormControls.Row>
                <FormControls.FormText>Backfill days:</FormControls.FormText>
                <Tooltip title={timeBucketDisabledReason ?? ''}>
                  <TextField
                    sx={{ width: '50px' }}
                    defaultValue={2}
                    disabled={!!timeBucketDisabledReason}
                    {...register('backfillDays')}
                  />
                </Tooltip>
                <InfoTooltip title="To ensure accuracy, metrics are recalculated for the last 2 days upon every run and are cached to shorten test running time" />
              </FormControls.Row>
              <Controller
                name="whereClause"
                control={control}
                render={({ field }) => <WhereClauseControl {...field} />}
              />
            </FlexColumn>
          </FormControls.TestAccordion>
        </FormControls.FieldsWrapper>

        <TestMetadataForm methods={methods} />
      </FlexColumn>
    </FormControls.TestAccordion>
  );
};

export default TablesDimensionAnomalies;
